import { useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { useAccounts, useAddSplits, useCategories, useCreateTransaction } from '@/lib/queries';
import { localTodayIso, money, nowTime } from '@/lib/format';
import { emptyDraft, splitProblem, toShares, type SplitDraft } from '@/lib/split';
import { useAuth } from '@/lib/auth';
import { Button, ErrorBox, Label, Modal, Spinner, Toggle, fieldStyles } from './ui';
import { SplitEditor } from './SplitEditor';
import { Select } from './Select';
import { DatePicker } from './DatePicker';

export function RecordModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { user } = useAuth();
  const { data: categories } = useCategories();
  const { data: accountData } = useAccounts();
  const accounts = accountData?.accounts;
  const create = useCreateTransaction();
  const addSplits = useAddSplits();

  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(localTodayIso());
  const [time, setTime] = useState(nowTime());
  const [merchant, setMerchant] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  // Set when the server guesses a category, cleared once the user picks one.
  const [autoTag, setAutoTag] = useState<string | null>(null);
  const [userPicked, setUserPicked] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [splitRows, setSplitRows] = useState<SplitDraft[]>([]);
  /*
   * The transaction, once it exists. Splits are a second call — if that one
   * fails, this is what stops "try again" from recording the payment twice.
   */
  const [savedId, setSavedId] = useState<string | null>(null);

  const currency = user?.currency ?? 'USD';
  const parsedAmount = Number(amount.replace(/[^0-9.\-]/g, ''));
  const totalCents = Number.isFinite(parsedAmount) ? Math.round(Math.abs(parsedAmount) * 100) : 0;
  // Splitting says "these people owe me part of what I paid", which money
  // coming in can't mean.
  const isIncome = categories?.find((c) => c.id === categoryId)?.kind === 'INCOME';

  useEffect(() => {
    if (!accountId && accounts?.length) setAccountId(accounts[0]!.id);
  }, [accounts, accountId]);

  // Ask the server what it would categorize this merchant as, debounced so
  // typing doesn't spam the endpoint.
  useEffect(() => {
    if (userPicked || merchant.trim().length < 3) {
      setAutoTag(null);
      return;
    }
    const handle = setTimeout(() => {
      void api<{ categoryId: string | null; confidence: number }>(
        `/transactions/suggest?merchant=${encodeURIComponent(merchant.trim())}`,
      )
        .then((guess) => {
          if (!guess.categoryId) {
            setAutoTag(null);
            return;
          }
          const match = categories?.find((c) => c.id === guess.categoryId);
          if (match) {
            setCategoryId(guess.categoryId);
            setAutoTag(`auto: ${match.name} · ${guess.confidence}%`);
          }
        })
        .catch(() => setAutoTag(null));
    }, 350);
    return () => clearTimeout(handle);
  }, [merchant, categories, userPicked]);

  const shares = splitting && !isIncome ? splitRows : [];
  const busy = create.isPending || addSplits.isPending;

  async function submit() {
    setError('');
    if (!Number.isFinite(parsedAmount) || parsedAmount === 0) {
      setError('Enter an amount.');
      return;
    }
    if (!merchant.trim()) {
      setError('Add a merchant.');
      return;
    }
    if (!accountId) {
      setError('Pick an account.');
      return;
    }

    // Checked before anything is written, so a bad split never leaves a
    // half-recorded payment behind.
    const problem = shares.length ? splitProblem(shares, totalCents) : null;
    if (problem) {
      setError(problem);
      return;
    }

    try {
      // Already saved means a previous attempt got the payment in and only the
      // split failed; retrying must not record the payment a second time.
      const id =
        savedId ??
        (
          await create.mutateAsync({
            accountId,
            categoryId: categoryId || null,
            date,
            time: time || null,
            merchant: merchant.trim(),
            note: note.trim() || undefined,
            amount: parsedAmount,
          })
        ).transaction.id;
      setSavedId(id);

      if (shares.length) {
        const owed = await addSplits.mutateAsync({
          transactionId: id,
          people: toShares(shares, totalCents),
        });
        const owedCents = owed.splits.reduce((total, split) => total + split.amountCents, 0);
        onSaved(
          `Recorded. ${money(owedCents, currency)} owed back by ${owed.splits.length} ${
            owed.splits.length === 1 ? 'person' : 'people'
          } — settle each as they pay you.`,
        );
        return;
      }

      const category = categories?.find((c) => c.id === categoryId);
      onSaved(
        category?.kind === 'INCOME'
          ? 'Recorded. Your income total is updated.'
          : 'Recorded. Budgets and totals are updated.',
      );
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Couldn’t save that. Try again.';
      setError(savedId ? `The payment is saved, but the split didn’t go through. ${message}` : message);
    }
  }

  return (
    <Modal title="Record a transaction" onClose={onClose}>
      <form
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Label htmlFor="amount">amount</Label>
            <input
              id="amount"
              className={fieldStyles.inputMono}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              inputMode="decimal"
              autoFocus
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label htmlFor="date">date</Label>
            <DatePicker id="date" value={date} onChange={setDate} max={localTodayIso()} ariaLabel="Transaction date" />
          </div>
        </div>

        <div>
          <Label htmlFor="time">time</Label>
          <input
            id="time"
            type="time"
            className={fieldStyles.inputTime}
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="merchant">merchant</Label>
          <input
            id="merchant"
            className={fieldStyles.input}
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
            placeholder="e.g. Trader Joe’s"
          />
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Label htmlFor="category">category</Label>
            {autoTag ? (
              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--accent-text)', marginBottom: 7 }}>
                {autoTag}
              </span>
            ) : null}
          </div>
          <Select
            id="category"
            ariaLabel="Category"
            value={categoryId}
            placeholder="Uncategorized"
            options={[
              { value: '', label: 'Uncategorized' },
              ...(categories ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
            onChange={(v) => {
              setCategoryId(v);
              setUserPicked(true);
              setAutoTag(null);
            }}
          />
        </div>

        <div>
          <Label htmlFor="account">account</Label>
          <Select
            id="account"
            ariaLabel="Account"
            value={accountId}
            options={(accounts ?? []).map((a) => ({ value: a.id, label: a.name }))}
            onChange={setAccountId}
          />
        </div>

        <div>
          <Label htmlFor="note">note (optional)</Label>
          <input
            id="note"
            className={fieldStyles.input}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What was it for?"
          />
        </div>

        {isIncome ? null : (
          <div
            style={{
              borderTop: '1px solid var(--border-soft)',
              paddingTop: 13,
              display: 'flex',
              flexDirection: 'column',
              gap: 11,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div>
                <div style={{ fontSize: 13 }}>Split it with people</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted-2)' }}>
                  Their shares become money owed to you.
                </div>
              </div>
              <span style={{ marginLeft: 'auto' }}>
                <Toggle
                  checked={splitting}
                  label="Split this payment with other people"
                  onChange={(next) => {
                    setSplitting(next);
                    if (next && splitRows.length === 0) setSplitRows([emptyDraft()]);
                    setError('');
                  }}
                />
              </span>
            </div>

            {splitting ? (
              <>
                <SplitEditor
                  rows={splitRows}
                  onChange={(rows) => {
                    setSplitRows(rows);
                    setError('');
                  }}
                  totalCents={totalCents}
                  currency={currency}
                />
                <div style={{ fontSize: 11.5, color: 'var(--muted-2)', textWrap: 'pretty' }}>
                  The full {totalCents ? money(totalCents, currency) : 'amount'} still leaves your
                  account now. Settling a share writes the repayment back in for you.
                </div>
              </>
            ) : null}
          </div>
        )}

        {error ? <ErrorBox>{error}</ErrorBox> : null}

        <div style={{ display: 'flex', gap: 9, marginTop: 4 }}>
          <Button variant="ghost" onClick={onClose} style={{ flex: '0 0 auto', padding: '11px 17px' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={busy}
            style={{ flex: 1, padding: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {busy ? <Spinner /> : null}
            {busy ? 'Saving…' : shares.length ? 'Save and split' : 'Save transaction'}
          </Button>
        </div>

        <div style={{ fontSize: 11.5, color: 'var(--muted-2)', textAlign: 'center', textWrap: 'pretty' }}>
          Expenses are stored as negative and income as positive — picking the category sets the sign.
        </div>
      </form>
    </Modal>
  );
}
